/* eslint-disable i18next/no-literal-string -- a code-only module: JSON keys, decklist marker syntax and set codes; it renders nothing */
// Per-card printing, foil and tags (deck editor Wave 4). Pure: no React, no
// storage, no I/O.
//
// Stored in saved_decks.card_meta (jsonb, NULL on every row until a new
// client writes it), keyed by the card's FRONT-FACE name as the editor names
// it:
//
//   {"Sol Ring": {"printing": {"scryfall_id": "...", "set": "cmm", "cn": "400"},
//                 "foil": true, "tags": ["Ramp", "Wincon"]}}
//
// Rules:
//  - Reads are lenient: anything malformed is dropped, never thrown on.
//  - Lookups are by DFC front face, case-insensitive (frontKey), the same
//    identity the editor's rows use.
//  - Entries for cards no longer in the list are ignored on read and pruned
//    on the next write (pruneCardMetaForText).
//  - Ownership stays BY NAME: nothing here touches have / need.
//  - The database CHECK caps the column at 262,144 bytes; the client refuses
//    anything over CARD_META_MAX_BYTES first, with a message.
import { frontKey, scanDecklist, stripNameMarkers } from './decklist'
import { parseDeckDocument } from './deckDocument'

export interface CardPrintingRef {
  /** Absent when the printing came from a text marker ("(CMM) 400"). */
  scryfall_id?: string
  /** Lowercase set code, as Scryfall writes it. */
  set: string
  /** Collector number, as written. */
  cn: string
}

export interface CardMetaEntry {
  printing?: CardPrintingRef
  foil?: boolean
  tags?: string[]
}

export type CardMetaMap = Record<string, CardMetaEntry>

/** Client-side cap (the database's CHECK is 262,144). */
export const CARD_META_MAX_BYTES = 200_000
export const MAX_TAGS_PER_CARD = 12
export const MAX_TAG_LENGTH = 24

const EMPTY: CardMetaMap = Object.freeze({}) as CardMetaMap

// The same shape stripNameMarkers strips ("(C21) 123"), so a stored printing
// always re-parses from its own marker.
const SET_RE = /^[a-z0-9]{2,6}$/
// Collector numbers as Scryfall writes them: "400", "123a", "123★", "1†",
// "GR7", "2023-1". Anything but the marker syntax and spaces.
// A lone bracketed token read as a legacy set code ("[C21]").
const LEGACY_SET_RE = /^[A-Z0-9]{3,5}$/
const CN_RE = /^[^\s,()[\]*]{1,16}$/

/** A set code as card_meta stores it (lowercase, 2 to 6; the rule its reads enforce). */
export function isPrintingSet(v: unknown): v is string {
  return typeof v === 'string' && SET_RE.test(v)
}

/** A collector number as card_meta stores it (the rule its reads enforce). */
export function isCollectorNumber(v: unknown): v is string {
  return typeof v === 'string' && CN_RE.test(v)
}
// Characters a tag may not hold: they are the decklist marker syntax
// ("[a, b]", "{noDeck}", "*F*", "^label^", "#").
const TAG_STRIP_RE = /[[\]{}(),*^#\n\r\t]/g
// Tags the decklist parsers read as a SECTION ("[Commander]" moves a card
// into the command zone, "{noDeck}" out of the deck): never a user tag.
const RESERVED_TAGS = new Set(['commander', 'commanders', 'maybeboard', 'sideboard', 'nodeck'])

/** The key a card's entry is stored under: its front face, as written. */
export function metaKeyOf(name: string): string {
  return name.split('//')[0].trim()
}

/** A tag as stored, or null when nothing usable is left (or it is reserved). */
export function normalizeTag(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const t = raw.replace(TAG_STRIP_RE, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_LENGTH).trim()
  if (!t) return null
  if (RESERVED_TAGS.has(t.toLowerCase())) return null
  return t
}

/** Tags normalized, de-duplicated case-insensitively (first spelling kept), capped. */
export function normalizeTags(raw: readonly unknown[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const r of raw) {
    const t = normalizeTag(r)
    if (!t || seen.has(t.toLowerCase())) continue
    seen.add(t.toLowerCase())
    out.push(t)
    if (out.length >= MAX_TAGS_PER_CARD) break
  }
  return out
}

function normalizePrinting(raw: unknown): CardPrintingRef | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const r = raw as Record<string, unknown>
  const set = typeof r.set === 'string' ? r.set.trim().toLowerCase() : ''
  const cn = typeof r.cn === 'string' ? r.cn.trim() : typeof r.cn === 'number' && Number.isFinite(r.cn) ? String(r.cn) : ''
  if (!SET_RE.test(set) || !CN_RE.test(cn)) return undefined
  const id = typeof r.scryfall_id === 'string' ? r.scryfall_id.trim() : ''
  return id && id.length <= 64 ? { scryfall_id: id, set, cn } : { set, cn }
}

/** One entry, sanitized; null when nothing valid is left. */
export function normalizeEntry(raw: unknown): CardMetaEntry | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const out: CardMetaEntry = {}
  const printing = normalizePrinting(r.printing)
  if (printing) out.printing = printing
  if (r.foil === true) out.foil = true
  if (Array.isArray(r.tags)) {
    const tags = normalizeTags(r.tags)
    if (tags.length > 0) out.tags = tags
  }
  return isEmptyEntry(out) ? null : out
}

export function isEmptyEntry(e: CardMetaEntry | null | undefined): boolean {
  return !e || (!e.printing && !e.foil && !(e.tags && e.tags.length > 0))
}

/**
 * card_meta as read (a jsonb object, a JSON string, null or junk) as a map.
 * Never throws: a malformed value is an empty map, a malformed entry is
 * dropped, two keys naming one card keep the first.
 */
export function parseCardMeta(raw: unknown): CardMetaMap {
  let v = raw
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v)
    } catch {
      return EMPTY
    }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return EMPTY
  const out: CardMetaMap = {}
  const seen = new Set<string>()
  for (const [k, e] of Object.entries(v as Record<string, unknown>)) {
    const key = metaKeyOf(k)
    if (!key || key.length > 200) continue
    const fk = frontKey(key)
    if (seen.has(fk)) continue
    const entry = normalizeEntry(e)
    if (!entry) continue
    seen.add(fk)
    out[key] = entry
  }
  return out
}

export function isEmptyMeta(meta: CardMetaMap | null | undefined): boolean {
  return !meta || Object.keys(meta).length === 0
}

/** The entry for a card (any spelling of its front face), or null. */
export function metaEntryFor(meta: CardMetaMap | null | undefined, name: string): CardMetaEntry | null {
  if (!meta) return null
  const direct = meta[metaKeyOf(name)]
  if (direct) return direct
  const k = frontKey(name)
  for (const [key, e] of Object.entries(meta)) if (frontKey(key) === k) return e
  return null
}

/** front key -> entry, for per-row lookups over a whole list. */
export function metaIndex(meta: CardMetaMap | null | undefined): Map<string, CardMetaEntry> {
  const out = new Map<string, CardMetaEntry>()
  for (const [key, e] of Object.entries(meta ?? {})) {
    const k = frontKey(key)
    if (!out.has(k)) out.set(k, e)
  }
  return out
}

/** A new map with one card's entry replaced (null / empty: removed). */
export function setMetaEntry(meta: CardMetaMap | null | undefined, name: string, entry: CardMetaEntry | null): CardMetaMap {
  const k = frontKey(name)
  const out: CardMetaMap = {}
  for (const [key, e] of Object.entries(meta ?? {})) if (frontKey(key) !== k) out[key] = e
  const clean = entry ? normalizeEntry(entry) : null
  if (clean) out[metaKeyOf(name)] = clean
  return out
}

/** Only the entries for `names` (front-face compare). */
export function pruneCardMeta(meta: CardMetaMap | null | undefined, names: Iterable<string>): CardMetaMap {
  const keep = new Set<string>()
  for (const n of names) keep.add(frontKey(n))
  const out: CardMetaMap = {}
  for (const [key, e] of Object.entries(meta ?? {})) if (keep.has(frontKey(key))) out[key] = e
  return out
}

/** Every card a decklist text holds: command zone, companions, deck and Maybeboard. */
export function cardNamesOfText(text: string | null | undefined): string[] {
  const doc = parseDeckDocument(text ?? '')
  return [...doc.commandZone, ...doc.companions, ...doc.main.map(c => c.name), ...doc.maybe.map(c => c.name)]
}

/** The entries for the cards `text` holds (the write-time prune). */
export function pruneCardMetaForText(meta: CardMetaMap | null | undefined, text: string | null | undefined): CardMetaMap {
  return pruneCardMeta(meta, cardNamesOfText(text))
}

function canonicalEntry(e: CardMetaEntry): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (e.foil) out.foil = true
  if (e.printing) {
    out.printing = e.printing.scryfall_id
      ? { cn: e.printing.cn, scryfall_id: e.printing.scryfall_id, set: e.printing.set }
      : { cn: e.printing.cn, set: e.printing.set }
  }
  if (e.tags && e.tags.length > 0) out.tags = [...e.tags]
  return out
}

/**
 * A stable string for comparing two maps ('' for an empty one): keys sorted
 * by front face, entry fields in a fixed order. Two equal maps always give
 * the same string, whatever order they were built in.
 */
export function canonicalCardMeta(meta: CardMetaMap | null | undefined): string {
  if (!meta) return ''
  const keys = Object.keys(meta).filter(k => !isEmptyEntry(meta[k])).sort((a, b) => (frontKey(a) < frontKey(b) ? -1 : frontKey(a) > frontKey(b) ? 1 : 0))
  if (keys.length === 0) return ''
  const out: Record<string, unknown> = {}
  for (const k of keys) out[k] = canonicalEntry(meta[k])
  return JSON.stringify(out)
}

export function sameCardMeta(a: CardMetaMap | null | undefined, b: CardMetaMap | null | undefined): boolean {
  return canonicalCardMeta(a) === canonicalCardMeta(b)
}

/** The value a write stores: null for an empty map (the column's "none"). */
export function storedCardMeta(meta: CardMetaMap | null | undefined): CardMetaMap | null {
  const s = canonicalCardMeta(meta)
  return s ? (JSON.parse(s) as CardMetaMap) : null
}

/** The UTF-8 size of what a write would store. */
export function cardMetaBytes(meta: CardMetaMap | null | undefined): number {
  const s = canonicalCardMeta(meta)
  return s ? new TextEncoder().encode(s).length : 0
}

export function cardMetaTooLarge(meta: CardMetaMap | null | undefined): boolean {
  return cardMetaBytes(meta) > CARD_META_MAX_BYTES
}

/** Every tag used across the map (first spelling kept), sorted. */
export function deckTags(meta: CardMetaMap | null | undefined, names?: Iterable<string>): string[] {
  const only = names ? new Set([...names].map(frontKey)) : null
  const seen = new Map<string, string>()
  for (const [key, e] of Object.entries(meta ?? {})) {
    if (only && !only.has(frontKey(key))) continue
    for (const t of e.tags ?? []) if (!seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t)
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b))
}

// ---- Decklist text markers --------------------------------------------------
//
//   1 Sol Ring (CMM) 400 *F* [Ramp, Wincon]
//
// "(SET) CN" is the printing, "*F*" foil, "[a, b]" the tags. The same syntax
// Moxfield / Archidekt / ManaBox write, and the API's parse strips every one
// of them from the name.

/** The markers for one entry (' (CMM) 400 *F* [Ramp]'), '' when it has none. */
export function formatMetaMarkers(
  entry: CardMetaEntry | null | undefined,
  opts: { printing?: boolean; foil?: boolean; tags?: boolean } = {},
): string {
  if (!entry) return ''
  const { printing = true, foil = true, tags = true } = opts
  let out = ''
  if (printing && entry.printing) out += ` (${entry.printing.set.toUpperCase()}) ${entry.printing.cn}`
  if (foil && entry.foil) out += ' *F*'
  if (tags && entry.tags && entry.tags.length > 0) {
    // A lone "[RAMP]" with no printing before it reads as a legacy set code
    // ("[C21]"); a trailing comma keeps it a tag group ("[RAMP,]").
    const lone = entry.tags.length === 1 && !(printing && entry.printing) && LEGACY_SET_RE.test(entry.tags[0])
    out += ` [${entry.tags.join(', ')}${lone ? ',' : ''}]`
  }
  return out
}

const PRINTING_MARK_RE = /\s*\(([A-Za-z0-9]{2,6})\)\s*([^\s,()[\]*]{1,16})?\s*$/
const STAR_MARK_RE = /\s*\*([^*]+)\*\s*$/
const BRACKET_MARK_RE = /\s+\[([^[\]]*)\]\s*$/
const LABEL_MARK_RE = /\s*\^[^^]*\^\s*$/

/**
 * The markers on one card line's text after its count ("Sol Ring (CMM) 400
 * *F* [Ramp]"), in any order, as an entry; null when it carries none. The
 * name is stripNameMarkers', so it always matches the row the parse made.
 */
export function parseLineMarkers(rest: string): { name: string; entry: CardMetaEntry | null } {
  const name = stripNameMarkers(rest)
  let s = rest.trim()
  let printing: CardPrintingRef | undefined
  let foil = false
  const tagGroups: string[] = []
  const brackets: string[] = []
  for (let guard = 0; guard < 20; guard++) {
    let m: RegExpExecArray | null
    if ((m = STAR_MARK_RE.exec(s))) {
      if (/^f$/i.test(m[1].trim())) foil = true
      s = s.slice(0, m.index)
      continue
    }
    if ((m = BRACKET_MARK_RE.exec(s)) && s.slice(0, m.index).trim()) {
      brackets.push(m[1])
      s = s.slice(0, m.index)
      continue
    }
    if ((m = LABEL_MARK_RE.exec(s)) && s.slice(0, m.index).trim()) {
      s = s.slice(0, m.index)
      continue
    }
    if ((m = PRINTING_MARK_RE.exec(s)) && s.slice(0, m.index).trim()) {
      if (!printing && m[2]) printing = { set: m[1].toLowerCase(), cn: m[2] }
      s = s.slice(0, m.index)
      continue
    }
    break
  }
  // Brackets were read from the end: the group nearest the name comes first.
  for (const b of [...brackets].reverse()) {
    // A lone "[C21]" with no "(SET) CN" printing is a legacy set code.
    if (!printing && !b.includes(',') && LEGACY_SET_RE.test(b.trim())) continue
    tagGroups.push(...b.split(',').map(p => p.replace(/\{[^}]*\}/g, '')))
  }
  const tags = normalizeTags(tagGroups)
  const entry = normalizeEntry({ printing, foil, tags })
  return { name, entry }
}

const QTY_RE = /^(\d+)\s*[xX]?\s+(.+)$/

/**
 * The entries a decklist TEXT's own markers carry: every deck card line
 * (command zone, companions, the deck) and every Maybeboard line; never the
 * sideboard or tokens. The first line naming a card wins.
 */
export function cardMetaFromText(text: string | null | undefined, opts: { sections?: readonly string[] } = {}): CardMetaMap {
  const sections = new Set(opts.sections ?? ['main', 'commander', 'companion', 'maybeboard'])
  const out: CardMetaMap = {}
  const seen = new Set<string>()
  for (const l of scanDecklist(text ?? '')) {
    if (l.kind !== 'card' || !sections.has(l.section)) continue
    const m = QTY_RE.exec(l.line)
    const { name, entry } = parseLineMarkers(m ? m[2] : l.line)
    if (!name) continue
    const k = frontKey(name)
    if (seen.has(k)) continue
    seen.add(k)
    if (entry) out[metaKeyOf(name)] = entry
  }
  return out
}

/** The three fields a delta moves on its own. */
const META_FIELDS = ['printing', 'foil', 'tags'] as const
type MetaField = (typeof META_FIELDS)[number]

/** One field of an entry as a comparable string ('' = not set). */
// `loose`: a printing by set and number only (a text marker cannot carry the
// Scryfall id, so the same printing with and without one is not a conflict).
function fieldKey(e: CardMetaEntry | null | undefined, f: MetaField, loose = false): string {
  if (!e) return ''
  switch (f) {
    case 'printing': {
      const p = e.printing
      return p ? JSON.stringify([p.set, p.cn.toLowerCase(), loose ? '' : p.scryfall_id ?? '']) : ''
    }
    case 'foil':
      return e.foil ? '1' : ''
    case 'tags':
      return e.tags && e.tags.length > 0 ? JSON.stringify(e.tags) : ''
  }
}

export interface CardMetaDeltaResult {
  /** `fresh` with the delta applied (on a conflict, `next`'s value for that field). */
  meta: CardMetaMap
  /**
   * Cards (as named in base / next) where `fresh` ALSO moved a field the
   * delta moves, to a different value: both sides edited the same field.
   */
  conflicts: string[]
}

/**
 * The delta from `base` to `next`, applied PER FIELD onto `fresh`: for each
 * card, each of printing, foil and tags that moved from `base` to `next` is
 * set to its `next` value; every field the delta did not move keeps
 * `fresh`'s value; cards the delta does not name are untouched. A field that
 * moved on BOTH sides (fresh differs from base, and from next) is a conflict.
 */
export function mergeCardMetaDelta(fresh: CardMetaMap | null | undefined, base: CardMetaMap | null | undefined, next: CardMetaMap | null | undefined): CardMetaDeltaResult {
  const b = metaIndex(base)
  const n = metaIndex(next)
  const f = metaIndex(fresh)
  const names = new Map<string, string>()
  for (const k of [...Object.keys(base ?? {}), ...Object.keys(next ?? {})]) if (!names.has(frontKey(k))) names.set(frontKey(k), k)
  let out: CardMetaMap = { ...(fresh ?? {}) }
  const conflicts: string[] = []
  for (const [fk, name] of names) {
    const was = b.get(fk) ?? null
    const now = n.get(fk) ?? null
    const cur = f.get(fk) ?? null
    let moved = false
    let conflict = false
    const entry: CardMetaEntry = { ...(cur ?? {}) }
    for (const field of META_FIELDS) {
      const w = fieldKey(was, field)
      const v = fieldKey(now, field)
      if (w === v) continue
      moved = true
      const c = fieldKey(cur, field, true)
      if (c !== fieldKey(was, field, true) && c !== fieldKey(now, field, true)) conflict = true
      if (field === 'printing') {
        const np = now?.printing
        const cp = cur?.printing
        // An equivalent marker printing (same set and number, no Scryfall
        // id: a marker cannot carry one) keeps the stored printing and its id.
        if (np) entry.printing = cp?.scryfall_id && !np.scryfall_id && cp.set === np.set && cp.cn.toLowerCase() === np.cn.toLowerCase() ? cp : np
        else delete entry.printing
      } else if (field === 'foil') {
        if (now?.foil) entry.foil = true
        else delete entry.foil
      } else if (now?.tags && now.tags.length > 0) entry.tags = [...now.tags]
      else delete entry.tags
    }
    if (!moved) continue
    if (conflict) conflicts.push(name)
    out = setMetaEntry(out, name, entry)
  }
  return { meta: out, conflicts }
}

/**
 * mergeCardMetaDelta's map, with `next` winning any conflicting field: the
 * merge an explicit user choice gets (restoring a draft the user picked).
 * A save must use mergeCardMetaDelta and refuse on a conflict instead.
 */
export function applyCardMetaDelta(fresh: CardMetaMap | null | undefined, base: CardMetaMap | null | undefined, next: CardMetaMap | null | undefined): CardMetaMap {
  return mergeCardMetaDelta(fresh, base, next).meta
}

/** `base` with every entry of `over` on top (a card's whole entry replaced). */
export function mergeCardMeta(base: CardMetaMap | null | undefined, over: CardMetaMap | null | undefined): CardMetaMap {
  let out: CardMetaMap = { ...(base ?? {}) }
  for (const [key, e] of Object.entries(over ?? {})) out = setMetaEntry(out, key, e)
  return out
}

/**
 * A text-marker entry against the one already held: the same set and number
 * keep the held Scryfall id (a marker cannot carry it).
 */
export function adoptMarkerEntry(held: CardMetaEntry | null, marker: CardMetaEntry | null): CardMetaEntry | null {
  if (!marker) return null
  const hp = held?.printing
  const mp = marker.printing
  if (hp && mp && hp.scryfall_id && hp.set === mp.set && hp.cn.toLowerCase() === mp.cn.toLowerCase()) {
    return { ...marker, printing: { ...mp, scryfall_id: hp.scryfall_id } }
  }
  return marker
}

/**
 * Text mode's main-deck lines with the held entries written on them
 * ("1 Sol Ring" -> "1 Sol Ring (CMM) 400 *F* [Ramp]"). Only a plain
 * "N Name" main-deck line is decorated; every other line is unchanged, so a
 * map with no entries returns `text` byte for byte.
 */
export function decorateDeckText(text: string, meta: CardMetaMap | null | undefined): string {
  if (isEmptyMeta(meta)) return text
  const idx = metaIndex(meta)
  const scanned = scanDecklist(text)
  const lines = text.split(/\r?\n/)
  if (scanned.length !== lines.length) return text
  return lines.map((raw, i) => {
    const l = scanned[i]
    if (l.kind !== 'card' || l.section !== 'main') return raw
    const m = QTY_RE.exec(l.line)
    if (!m || stripNameMarkers(m[2]) !== m[2].trim()) return raw
    const e = idx.get(frontKey(m[2]))
    return e ? `${raw.trimEnd()}${formatMetaMarkers(e)}` : raw
  }).join('\n')
}
